import { Box, Grid2, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';
import StackedIcon from '@components/Icon/StackedIcon';
import NullStateDiv from '@components/NullStateDiv';

import { VITAL_SIGN_TYPES } from '@constants/fhir';

import { useSnackbar } from '@hooks/useSnackbar';

import { VitalSignObservationRecord } from '@models/vital-sign';

import { formatDate, formatVitalSign } from '@utils/format';

import { useListActivityObservationsLazyQuery } from 'generated/activity.generated';
import type { ActivityModel } from 'generated/types';

import { FontWeight } from '../../theme/typography';
import CollapsibleCard from './CollapsibleCard';

const messages = defineMessages({
  fallback: {
    id: 'terra.enums.activityType.fallback',
    defaultMessage: 'Unknown Activity',
  },
  noMetrics: {
    id: 'activityMain.noMetrics',
    defaultMessage: 'No Metrics',
  },
  error: {
    id: 'activityMain.observationsError',
    defaultMessage:
      "An error occurred when attempting to display an activity's metrics",
  },
});

interface ActivityCardProps {
  data: ActivityModel;
  loading: boolean;
  isExpanded?: boolean;
}
export default function ActivityCard({
  data: {
    activity_type,
    device_name,
    end_time,
    start_time,
    summary_id,
    terra_connection_id,
  },
  loading: activityLoading,
  isExpanded,
}: ActivityCardProps) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [
    observationsQuery,
    { data: summaryObservationsData, loading: summaryObservationsLoading },
  ] = useListActivityObservationsLazyQuery();

  const summaryObservations = (
    summaryObservationsData?.getTerraActivity.observations ?? []
  ).map((o) => new VitalSignObservationRecord(o));

  useEffect(() => {
    if (isExpanded) {
      handleExpanded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const calculateDuration = () => {
    const diff = dayjs(end_time).diff(dayjs(start_time), 'minute', true);
    const hours = Math.floor(diff / 60);
    const minutes = Math.round(diff - hours * 60);

    return `${hours > 0 ? `${hours}hr` : ''} ${
      minutes > 0 ? `${minutes}min` : ''
    }`;
  };

  async function handleExpanded() {
    if (!summary_id || !terra_connection_id) {
      enqueueSnackbar({
        message: formatMessage(messages.error),
        variant: 'error',
      });
      return;
    }

    try {
      await observationsQuery({
        variables: {
          input: {
            summary_id,
            terra_connection_id,
          },
        },
      });
    } catch (e) {
      enqueueSnackbar({
        message: formatMessage(messages.error),
        variant: 'error',
      });
    }
  }

  const cardContent = (
    <ContentLoader loading={!summary_id && activityLoading}>
      <Grid2 container>
        <Grid2 sx={{ flexGrow: 1 }}>
          <Typography variant="overline">{device_name}</Typography>
          <Typography variant="h5">
            {formatMessage({
              id: `terra.enums.activityType.${activity_type}`,
              defaultMessage: formatMessage(messages.fallback),
            })}
          </Typography>
        </Grid2>

        <Grid2
          sx={{
            color: 'grey.500',
          }}
        >
          <Stack gap={0.5} alignItems="flex-end">
            <Typography
              variant="subtitle2"
              textAlign="end"
              fontWeight={FontWeight.Light}
            >
              {formatDate({ date: start_time, formatter: 'datetime_short' })}
            </Typography>
            <Stack direction="row" alignItems={'center'} gap={0.5}>
              <Icon clickable color="grey.500" name="clock" type="far" />
              <Typography variant="subtitle2" fontWeight={FontWeight.Light}>
                {calculateDuration()}
              </Typography>
            </Stack>
          </Stack>
        </Grid2>
      </Grid2>
    </ContentLoader>
  );

  const expandedContent = (
    <ContentLoader loading={summaryObservationsLoading}>
      {summaryObservations.length > 0 ? (
        <Grid2 container>
          {summaryObservations.map((observation) => {
            const { vitallCode, id, unit } = observation;
            const signType = VITAL_SIGN_TYPES[vitallCode];
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!signType) {
              return null;
            }
            const { icon, format, multiplier, unit: obsUnit } = signType;
            return (
              <Grid2 flex={1} key={`activity-observation-${id}`}>
                <Stack alignItems="center" direction="row" gap={0.25}>
                  <Box sx={{ width: 40, textAlign: 'center', fontSize: 24 }}>
                    <StackedIcon
                      icons={icon.map((i) => ({ size: '1x', ...i }))}
                    />
                  </Box>
                  <Stack alignItems="baseline" direction="row" gap={0.25}>
                    <Typography variant="h6" fontWeight={FontWeight.Light}>
                      {formatVitalSign(observation, false, format, multiplier)}
                    </Typography>
                    <Typography
                      color="grey.500"
                      variant="subtitle2"
                      fontWeight={FontWeight.Light}
                    >
                      {unit ?? obsUnit}
                    </Typography>
                  </Stack>
                </Stack>
              </Grid2>
            );
          })}
        </Grid2>
      ) : (
        <NullStateDiv sx={{ minHeight: 76, p: 2 }}>
          {formatMessage(messages.noMetrics)}
        </NullStateDiv>
      )}
    </ContentLoader>
  );

  return (
    <CollapsibleCard
      cardContent={cardContent}
      expandedContent={expandedContent}
      isExpanded={isExpanded}
      onExpand={handleExpanded}
    />
  );
}
